import { useState, useEffect, useRef } from "react";
import { RiCloseLargeFill, RiDeleteBinLine, RiAddFill } from "react-icons/ri";
import DynamicSelect from "../../DynamicSelect";
import { toast } from "react-toastify";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PrintBillLayoutA5 } from "../../PrintLayoutA5";

const API_BASE_URL = "https://api.muktihospital.com/api";
const AUTHORITIES_API_URL = "https://api.muktihospital.com/api/authorities";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const getLocalDateString = (dateInput = new Date()) => {
  const date = new Date(dateInput);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForPrint = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB");
};

const HospitalBillModal = ({
  isOpen,
  onClose,
  billId,
  initialData,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    specialNo: "",
    regNo: "",
    patientName: "",
    consultantName: "",
    admissionDate: "",
    billingDate: getLocalDateString(),
    refer: "",
    referAmount: "",
    billNumber: "",
    authorizedBy: "",
  });
  const [selectedBillId, setSelectedBillId] = useState(billId || null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [serviceCharge, setServiceCharge] = useState("0");
  const [discount, setDiscount] = useState("0");
  const [paidAmount, setPaidAmount] = useState("0");
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [billResponse, setBillResponse] = useState({
    billAmount: 0,
    serviceChargePc: 0,
    serviceCharge: 0,
    totalAmount: 0,
    discountPc: 0,
    discount: 0,
    netAmount: 0,
    paidAmount: 0,
    payableAmount: 0,
  });
  const [authorizedByOptions, setAuthorizedByOptions] = useState([]);
  const [regNoSearch, setRegNoSearch] = useState("");
  const [regNoOptions, setRegNoOptions] = useState([]);
  const [allBills, setAllBills] = useState([]);
  const [billExists, setBillExists] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);
  const searchRef = useRef(null);

  const calculateBillAmounts = (billData) => {
    let billAmount = 0;
    if (billData.serviceItems && Array.isArray(billData.serviceItems)) {
      billAmount = billData.serviceItems.reduce(
        (sum, item) => sum + item.unitPrice * item.totalUnit,
        0,
      );
    }

    const serviceChargeVal = parseFloat(billData.serviceCharge || 0);
    const totalAmount = billAmount + serviceChargeVal;

    const discountVal = parseFloat(billData.discount || 0);
    const netAmount = totalAmount - discountVal;

    const paidAmount = parseFloat(billData.paidAmount || 0);
    const payableAmount = netAmount - paidAmount;

    return {
      billAmount: Math.round(billAmount),
      serviceCharge: Math.round(serviceChargeVal),
      totalAmount: Math.round(totalAmount),
      discount: Math.round(discountVal),
      netAmount: Math.round(netAmount),
      paidAmount: Math.round(paidAmount),
      payableAmount: Math.round(payableAmount),
    };
  };

  useEffect(() => {
    const calculatedAmounts = calculateBillAmounts({
      serviceItems: tableData,
      serviceCharge,
      discount,
      paidAmount: parseFloat(paidAmount) || 0,
    });
    setBillResponse((prev) => ({
      ...prev,
      ...calculatedAmounts,
    }));
  }, [tableData, serviceCharge, discount, paidAmount]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          admissionsResp,
          servicesResp,
          agentsResp,
          billsResp,
          authoritiesResp,
          doctorsResp,
        ] = await Promise.all([
          axios.get(`${API_BASE_URL}/hospital/admission`, {
            params: { limit: "0" },
            headers: { "x-api-key": API_KEY },
          }),
          axios.get(`${API_BASE_URL}/hospital/service`, {
            headers: { "x-api-key": API_KEY },
          }),
          axios.get(`${API_BASE_URL}/hospital/agent`, {
            headers: { "x-api-key": API_KEY },
          }),
          axios.get(`${API_BASE_URL}/hospital/hospital-bills`, {
            headers: { "x-api-key": API_KEY },
          }),
          axios.get(AUTHORITIES_API_URL, {
            headers: { "x-api-key": API_KEY },
          }),
          axios.get(`${API_BASE_URL}/doctor`, {
            headers: { "x-api-key": API_KEY },
          }),
        ]);

        const admissionsData = Array.isArray(admissionsResp.data?.admissions)
          ? admissionsResp.data.admissions
          : Array.isArray(admissionsResp.data?.data?.admissions)
            ? admissionsResp.data.data.admissions
            : [];
            
        const mappedPatients = admissionsData.map((admission) => ({
          value: admission.id,
          label: `${admission.patientName || "Unknown"} (${admission.regNo || "No Reg"})`,
          nameOnly: admission.patientName || "Unknown",
          patientId: admission.patientId || admission.id,
          phoneNumber: admission.phoneNumber || "",
          specialNo: admission.specialNo || "",
          regNo: admission.regNo || "",
          sex: admission.sex || "",
          age: admission.age || "",
          admissionDate: admission.admissionDate
            ? admission.admissionDate.split("T")[0]
            : "",
          consultantName: admission.consultantName || "",
          agentName: admission.agentName || "",
          status: admission.status || "",
        }));

        mappedPatients.sort((a, b) => {
          const getNumber = (str) => {
            const match = (str || "").match(/(\d+)/);
            return match ? parseInt(match[0], 10) : 0;
          };
          return getNumber(a.regNo) - getNumber(b.regNo);
        });

        setPatients(mappedPatients);

        const authorityList = Array.isArray(authoritiesResp.data) ? authoritiesResp.data : [];
        setAuthorizedByOptions(authorityList.map((auth) => ({ value: auth.name, label: auth.name })));

        const servicesData = Array.isArray(servicesResp.data?.services)
          ? servicesResp.data.services
          : Array.isArray(servicesResp.data?.data?.services)
            ? servicesResp.data.data.services
            : [];
        setServices(servicesData.map((service) => ({
            value: service.id || `temp-${Date.now()}`,
            label: service.serviceName || "Unknown",
            unitPrice: service.unitPrice || 0,
        })));

        const agentsData = Array.isArray(agentsResp.data?.agents)
          ? agentsResp.data.agents
          : Array.isArray(agentsResp.data?.data?.agents)
            ? agentsResp.data.data.agents
            : [];
        setAgents(agentsData.map((agent) => ({
            value: agent.id || `temp-${Date.now()}`,
            label: agent.agentName || "Unknown",
        })));

        const doctorsData = Array.isArray(doctorsResp.data) ? doctorsResp.data : doctorsResp.data.data || [];
        setConsultants(
          doctorsData.map((d) => {
            let name = d.name || "Unknown";
            if (d.translations) {
              try {
                const t = typeof d.translations === "string" ? JSON.parse(d.translations) : d.translations;
                name = t?.en?.name || name;
              } catch (e) {}
            }
            return { value: name, label: name };
          }),
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch data. Please try again.", { autoClose: 2000 });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (billId || initialData) {
        const loadBill = async () => {
          setIsLoading(true);
          try {
            const response = initialData
              ? { data: initialData }
              : await axios.get(
                  `${API_BASE_URL}/hospital/hospital-bills/${billId}`,
                  { headers: { "x-api-key": API_KEY } },
                );
            const bill = response.data.bill || response.data;
            setSelectedBillId(bill.id || billId);
            const agent = agents.find((a) => a.value === bill.refer);
            setFormData({
              specialNo: bill.specialNo || "",
              regNo: bill.regNo || "",
              patientName: bill.patientName || "",
              consultantName: bill.consultantName || "",
              admissionDate: bill.admissionDate ? getLocalDateString(bill.admissionDate) : "",
              billingDate: bill.billingDate ? getLocalDateString(bill.billingDate) : getLocalDateString(),
              refer: agent ? agent.label : bill.refer || "",
              referAmount: bill.referAmount || "",
              billNumber: bill.billNumber || "",
              authorizedBy: bill.authorizedBy || "",
            });
            setTableData(
              bill.serviceItems?.map((item) => ({
                serviceId: item.serviceName || "",
                unitPrice: item.unitPrice || 0,
                totalUnit: item.totalUnit || "",
                amount: item.amount || 0,
              })) || [{ serviceId: "", unitPrice: 0, totalUnit: "", amount: 0 }],
            );
            setServiceCharge(bill.serviceCharge || "0");
            setDiscount(bill.discount || "0");
            setPaidAmount(bill.paidAmount || "0");
            setBillResponse(bill);
            setBillExists(true);
          } catch (error) {
            console.error("Error loading bill:", error);
            toast.error("Failed to load bill data", { autoClose: 2000 });
          } finally {
            setIsLoading(false);
          }
        };
        loadBill();
      } else {
        handleReset();
        setSelectedBillId(null);
      }
    }
  }, [isOpen, billId, initialData, agents]);

  const fetchBillByRegNo = async (regNoToSearch) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/hospital/hospital-bills`, {
        params: { search: regNoToSearch },
        headers: { "x-api-key": API_KEY },
      });
      
      const fetchedBills = Array.isArray(response.data) 
        ? response.data 
        : response.data?.bills || response.data?.data?.bills || [];
        
      // Ensure exact match
      return fetchedBills.find((b) => b.regNo === regNoToSearch) || null;
    } catch (error) {
      console.error("Error searching for bill on server:", error);
      return null;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "regNoSearch") {
      setRegNoSearch(value);
      if (value) {
        const filtered = patients
          .filter((p) => p.regNo && p.regNo.toLowerCase().includes(value.toLowerCase()))
          .map((p) => p.regNo);
        setRegNoOptions([...new Set(filtered)]);
      } else {
        setRegNoOptions([]);
      }
    }

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (field) => async (selectedOption) => {
    if (field === "patientName" && selectedOption) {
      const selectedPatient = patients.find((p) => p.value === selectedOption.value);
      
      setFormData((prev) => ({
          ...prev,
          patientName: selectedOption.label,
          specialNo: selectedPatient?.specialNo || "",
          regNo: selectedPatient?.regNo || "",
          admissionDate: selectedPatient?.admissionDate || "",
          consultantName: selectedPatient?.consultantName || prev.consultantName,
          refer: selectedPatient?.agentName || "",
      }));
      
      setErrors((prev) => ({ ...prev, patientName: "", specialNo: "", regNo: "", admissionDate: "" }));

      const regNo = selectedPatient?.regNo || "";
      if (regNo) {
        setIsLoading(true);
        // ✅ Direct Server Search (Solves the Released Patient Bug)
        const bill = await fetchBillByRegNo(regNo);
        setBillExists(!!bill);

        if (bill) {
          try {
            const response = await axios.get(`${API_BASE_URL}/hospital/hospital-bills/${bill.id}`, {
                headers: { "x-api-key": API_KEY },
            });
            const fetchedBill = response.data.bill || response.data;
            
            setSelectedBillId(fetchedBill.id);
            const agent = agents.find((a) => a.value === fetchedBill.refer);
            
            setFormData((prev) => ({
              ...prev,
              specialNo: fetchedBill.specialNo || "",
              regNo: fetchedBill.regNo || "",
              patientName: fetchedBill.patientName || "",
              admissionDate: fetchedBill.admissionDate ? getLocalDateString(fetchedBill.admissionDate) : "",
              billingDate: fetchedBill.billingDate ? getLocalDateString(fetchedBill.billingDate) : prev.billingDate,
              refer: agent ? agent.label : fetchedBill.refer || "",
              referAmount: fetchedBill.referAmount || "",
              billNumber: fetchedBill.billNumber || "",
              authorizedBy: fetchedBill.authorizedBy || "",
              consultantName: fetchedBill.consultantName || selectedPatient?.consultantName || "",
            }));
            
            setTableData(
              fetchedBill.serviceItems?.map((item) => ({
                serviceId: item.serviceName || "",
                unitPrice: item.unitPrice || 0,
                totalUnit: item.totalUnit || "",
                amount: item.amount || 0,
              })) || [{ serviceId: "", unitPrice: 0, totalUnit: "", amount: 0 }],
            );
            setServiceCharge(fetchedBill.serviceCharge || "0");
            setDiscount(fetchedBill.discount || "0");
            setPaidAmount(fetchedBill.paidAmount || "0");
            setBillResponse(fetchedBill);
            toast.info("Existing Bill loaded for this patient.", { autoClose: 2000 });
          } catch (error) {
            console.error("Error loading bill details:", error);
            toast.error("Failed to load bill details.", { autoClose: 2000 });
          }
        } else {
          // Reset Calculations for New Bill
          setSelectedBillId(null);
          setTableData([{ serviceId: "", unitPrice: 0, totalUnit: "", amount: 0 }]);
          setServiceCharge("0");
          setDiscount("0");
          setPaidAmount("0");
          setBillResponse({
            billAmount: 0, serviceChargePc: 0, serviceCharge: 0, totalAmount: 0,
            discountPc: 0, discount: 0, netAmount: 0, paidAmount: 0, payableAmount: 0,
          });
        }
        setIsLoading(false);
      }
    } else if (field === "refer" && selectedOption) {
      setFormData((prev) => ({ ...prev, refer: selectedOption.label }));
      setErrors((prev) => ({ ...prev, refer: "" }));
    } else if (field === "consultantName" && selectedOption) {
      setFormData((prev) => ({ ...prev, consultantName: selectedOption.label }));
      setErrors((prev) => ({ ...prev, consultantName: "" }));
    } else if (field === "authorizedBy" && selectedOption) {
      setFormData((prev) => ({ ...prev, authorizedBy: selectedOption.label }));
      setErrors((prev) => ({ ...prev, authorizedBy: "" }));
    }
  };

  const handleTableInputChange = (index, e) => {
    const { name, value } = e.target;
    const newTableData = [...tableData];

    newTableData[index] = { ...newTableData[index], [name]: value };

    const unitPrice = parseFloat(newTableData[index].unitPrice) || 0;
    const totalUnit = parseFloat(newTableData[index].totalUnit) || 0;

    if (name === "unitPrice" || name === "totalUnit") {
      newTableData[index].amount = Math.round(unitPrice * totalUnit);
    }

    if (name === "totalUnit") {
      if (value && !isNaN(totalUnit) && totalUnit > 0) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          if (newTableData.every((row) => row.totalUnit && parseFloat(row.totalUnit) > 0)) {
            delete newErrors.totalUnit;
          }
          return newErrors;
        });
      }
    }

    setTableData(newTableData);
  };

  const handleServiceChange = (index) => (selectedOption) => {
    const serviceId = selectedOption ? selectedOption.value : "";
    const selectedService = services.find((service) => service.value === serviceId);

    const newTableData = [...tableData];
    newTableData[index] = {
      ...newTableData[index],
      serviceId: selectedService ? selectedService.label : "",
      unitPrice: selectedService?.unitPrice || 0,
      amount: Math.round(
        parseFloat(newTableData[index].totalUnit || 0) * (selectedService?.unitPrice || 0),
      ),
    };
    setTableData(newTableData);
  };

  const handleAddRow = () => {
    setTableData([...tableData, { serviceId: "", unitPrice: 0, totalUnit: "", amount: 0 }]);
  };

  const handleRemoveRow = (index) => {
    if (tableData.length > 1) {
      setTableData(tableData.filter((_, i) => i !== index));
    }
  };

  const handleOtherInputChange = (setter) => (e) => {
    setter(e.target.value);
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleReset = () => {
    setFormData({
      specialNo: "",
      regNo: "",
      patientName: "",
      admissionDate: "",
      billingDate: getLocalDateString(),
      refer: "",
      referAmount: "",
      billNumber: "",
      authorizedBy: "",
      consultantName: "",
    });
    setTableData([{ serviceId: "", unitPrice: 0, totalUnit: "", amount: 0 }]);
    setServiceCharge("0");
    setDiscount("0");
    setPaidAmount("0");
    setErrors({});
    setSelectedBillId(null);
    setBillResponse({
      billAmount: 0, serviceChargePc: 0, serviceCharge: 0, totalAmount: 0,
      discountPc: 0, discount: 0, netAmount: 0, paidAmount: 0, payableAmount: 0,
    });
    setRegNoSearch("");
    setRegNoOptions([]);
    setBillExists(false);
  };

  const handleSelectRegNo = async (selectedRegNo) => {
    if (!selectedRegNo) return;
    setRegNoSearch(selectedRegNo);
    setRegNoOptions([]);
    setIsLoading(true);

    try {
      const existingBill = await fetchBillByRegNo(selectedRegNo);

      if (existingBill) {
        const response = await axios.get(
          `${API_BASE_URL}/hospital/hospital-bills/${existingBill.id}`,
          { headers: { "x-api-key": API_KEY } },
        );
        const fetchedBill = response.data.bill || response.data;

        setSelectedBillId(fetchedBill.id);
        const agent = agents.find((a) => a.value === fetchedBill.refer);

        setFormData((prev) => ({
          ...prev,
          specialNo: fetchedBill.specialNo || "",
          regNo: fetchedBill.regNo || "",
          patientName: fetchedBill.patientName || "",
          admissionDate: fetchedBill.admissionDate ? getLocalDateString(fetchedBill.admissionDate) : "",
          billingDate: fetchedBill.billingDate ? getLocalDateString(fetchedBill.billingDate) : getLocalDateString(),
          refer: agent ? agent.label : fetchedBill.refer || "",
          referAmount: fetchedBill.referAmount || "",
          billNumber: fetchedBill.billNumber || "",
          authorizedBy: fetchedBill.authorizedBy || "",
          consultantName: fetchedBill.consultantName || "",
        }));

        setTableData(
          fetchedBill.serviceItems?.map((item) => ({
            serviceId: item.serviceName || "",
            unitPrice: item.unitPrice || 0,
            totalUnit: item.totalUnit || "",
            amount: item.amount || 0,
          })) || [{ serviceId: "", unitPrice: 0, totalUnit: "", amount: 0 }],
        );
        setServiceCharge(fetchedBill.serviceCharge || "0");
        setDiscount(fetchedBill.discount || "0");
        setPaidAmount(fetchedBill.paidAmount || "0");
        setBillResponse(fetchedBill);
        setBillExists(true);
        toast.info("Existing Bill loaded for this Reg No.", { autoClose: 2000 });
      } else {
        const patientData = patients.find((p) => p.regNo === selectedRegNo);

        if (patientData) {
          setFormData((prev) => ({
            ...prev,
            patientName: patientData.nameOnly,
            specialNo: patientData.specialNo || "",
            regNo: patientData.regNo || "",
            admissionDate: patientData.admissionDate || "",
            consultantName: patientData.consultantName || "",
            refer: patientData.agentName || "",
            billNumber: "",
            referAmount: "",
          }));

          setSelectedBillId(null);
          setTableData([{ serviceId: "", unitPrice: 0, totalUnit: "", amount: 0 }]);
          setServiceCharge("0");
          setDiscount("0");
          setPaidAmount("0");
          setBillExists(false);
          toast.success("Patient details loaded. Ready to generate bill.", { autoClose: 2000 });
        }
      }
    } catch (error) {
      console.error("Error selecting Reg No:", error);
      toast.error("Failed to load details", { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.specialNo) newErrors.specialNo = "Special No is required";
    if (!formData.authorizedBy) newErrors.authorizedBy = "Authorizer is required";
    if (!formData.regNo) newErrors.regNo = "Reg No is required";
    if (!formData.patientName) newErrors.patientName = "Patient name is required";
    if (!formData.admissionDate) newErrors.admissionDate = "Admission Date is required";
    if (!formData.billingDate) newErrors.billingDate = "Billing Date is required";
    if (tableData.some((row) => !row.serviceId)) newErrors.serviceId = "Service selection is required for all rows";
    if (tableData.some((row) => !row.totalUnit || parseFloat(row.totalUnit) <= 0)) {
      newErrors.totalUnit = "Total Unit must be a positive number for all rows";
    }
    if (!paidAmount) newErrors.paidAmount = "Paid amount is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields", { autoClose: 2000 });
      return;
    }

    setIsLoading(true);
    setProcessingAction("save");
    try {
      const payload = {
        specialNo: formData.specialNo,
        regNo: formData.regNo,
        admissionDate: formData.admissionDate,
        billingDate: formData.billingDate,
        refer: formData.refer,
        referAmount: parseFloat(formData.referAmount) || 0,
        serviceCharge: parseFloat(serviceCharge) || 0,
        discount: parseFloat(discount) || 0,
        serviceChargePc: 0,
        discountPc: 0,
        paidAmount: parseFloat(paidAmount) || 0,
        patientName: formData.patientName,
        authorizedBy: formData.authorizedBy,
        consultantName: formData.consultantName,
        serviceItems: tableData.map((row) => ({
          serviceName: row.serviceId,
          unitPrice: parseFloat(row.unitPrice) || 0,
          totalUnit: parseInt(row.totalUnit) || 0,
          amount: parseFloat(row.amount) || 0,
        })),
      };

      const existingBill = await fetchBillByRegNo(formData.regNo);
      if (existingBill && !selectedBillId) {
        toast.error("Bill for this registration number already exists! Please update the existing bill.", { autoClose: 2000 });
        setIsLoading(false);
        setProcessingAction(null);
        return;
      }

      let response;
      if (selectedBillId) {
        response = await axios.put(
          `${API_BASE_URL}/hospital/hospital-bills/edit/${selectedBillId}`,
          payload,
          { headers: { "x-api-key": API_KEY } }
        );
        toast.success("Bill updated successfully!", { autoClose: 2000 });
      } else {
        response = await axios.post(
          `${API_BASE_URL}/hospital/hospital-bills/generate`,
          payload,
          { headers: { "x-api-key": API_KEY } }
        );
        toast.success("Bill saved successfully!", { autoClose: 2000 });
      }
      
      const savedBill = response.data.bill || response.data;
      setBillResponse(savedBill);
      setSelectedBillId(savedBill.id);
      setFormData((prev) => ({ ...prev, billNumber: savedBill.billNumber || "" }));
      onSave(savedBill, false);
      
    } catch (error) {
      console.error("Error saving/updating bill:", error);
      setErrors({ apiError: error.response?.data?.error || "Failed to save/update bill" });
      toast.error("Failed to save/update bill", { autoClose: 2000 });
    } finally {
      setIsLoading(false);
      setProcessingAction(null);
    }
  };

  const handleSaveAndPrint = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.specialNo) newErrors.specialNo = "Special No is required";
    if (!formData.authorizedBy) newErrors.authorizedBy = "Authorizer is required";
    if (!formData.regNo) newErrors.regNo = "Reg No is required";
    if (!formData.patientName) newErrors.patientName = "Patient name is required";
    if (!formData.admissionDate) newErrors.admissionDate = "Admission Date is required";
    if (!formData.billingDate) newErrors.billingDate = "Billing Date is required";
    if (tableData.some((row) => !row.serviceId)) newErrors.serviceId = "Service selection is required for all rows";
    if (tableData.some((row) => !row.totalUnit || parseFloat(row.totalUnit) <= 0)) {
      newErrors.totalUnit = "Total Unit must be a positive number for all rows";
    }
    if (!paidAmount) newErrors.paidAmount = "Paid amount is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields", { autoClose: 2000 });
      return;
    }

    setIsLoading(true);
    setProcessingAction("print");
    try {
      const payload = {
        specialNo: formData.specialNo,
        regNo: formData.regNo,
        admissionDate: formData.admissionDate,
        billingDate: formData.billingDate,
        refer: formData.refer,
        referAmount: parseFloat(formData.referAmount) || 0,
        serviceCharge: parseFloat(serviceCharge) || 0,
        discount: parseFloat(discount) || 0,
        paidAmount: parseFloat(paidAmount) || 0,
        patientName: formData.patientName,
        authorizedBy: formData.authorizedBy,
        consultantName: formData.consultantName,
        serviceItems: tableData.map((row) => ({
          serviceName: row.serviceId,
          unitPrice: parseFloat(row.unitPrice) || 0,
          totalUnit: parseInt(row.totalUnit) || 0,
          amount: parseFloat(row.amount) || 0,
        })),
      };

      // ✅ Uses server search
      const existingBill = await fetchBillByRegNo(formData.regNo);
      if (existingBill && !selectedBillId) {
        toast.error("Bill for this registration number already exists! Please update the existing bill.", { autoClose: 2000 });
        setIsLoading(false);
        setProcessingAction(null);
        return;
      }

      let response;
      if (selectedBillId) {
        response = await axios.put(
          `${API_BASE_URL}/hospital/hospital-bills/edit/${selectedBillId}`,
          payload,
          { headers: { "x-api-key": API_KEY } }
        );
        toast.success("Bill updated successfully!", { autoClose: 2000 });
      } else {
        response = await axios.post(
          `${API_BASE_URL}/hospital/hospital-bills/generate`,
          payload,
          { headers: { "x-api-key": API_KEY } }
        );
        toast.success("Bill saved successfully!", { autoClose: 2000 });
      }

      const savedBill = response.data.bill || response.data;
      setBillResponse(savedBill);
      setSelectedBillId(savedBill.id);
      setFormData((prev) => ({ ...prev, billNumber: savedBill.billNumber || "" }));

      const signatureFooter = (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0px", paddingRight: "6px" }}>
          <div style={{ textAlign: "center", minWidth: "180px" }}>
            <p style={{ margin: "0 0 2px 0", fontWeight: "bold", fontSize: "9.5pt", color: "#323290", fontFamily: "cursive" }}>
              {formData.authorizedBy || "N/A"}
            </p>
            <div style={{ borderTop: "1.5px solid #323290", width: "160px", margin: "0 auto 3px auto" }}></div>
            <p style={{ margin: "0", fontSize: "8pt", fontWeight: "bold", letterSpacing: "0.5px", textTransform: "uppercase", color: "#374151" }}>
              Authorized By
            </p>
            <p style={{ margin: "1px 0 0 0", fontSize: "7pt", color: "#6b7280" }}>
              Mukti Hospital Pvt. Ltd.
            </p>
          </div>
        </div>
      );

      const billInfo = (
        <>
          <div>
            <p><strong>Bill No:</strong> {formData.billNumber || "N/A"}</p>
            <p><strong>Special No:</strong> {formData.specialNo || "N/A"}</p>
            <p><strong>Reg No:</strong> {formData.regNo || "N/A"}</p>
          </div>
          <div>
            <p><strong>Admission Date:</strong> {formatDateForPrint(formData.admissionDate)}</p>
            <p><strong>Billing Date:</strong> {formatDateForPrint(formData.billingDate)}</p>
            <p><strong>Bill Number:</strong> {savedBill.billNumber || "N/A"}</p>
          </div>
          <p className="full"><strong>Patient Name:</strong> {formData.patientName || "N/A"}</p>
          <p className="full"><strong>Consultant:</strong> {formData.consultantName || "N/A"}</p>
        </>
      );

      const table = (
        <table className="w-full border-collapse text-base">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border font-semibold">Sl No</th>
              <th className="p-2 border font-semibold">Service Name</th>
              <th className="p-2 border font-semibold text-right">Unit Price (৳)</th>
              <th className="p-2 border font-semibold text-right">Total Unit</th>
              <th className="p-2 border font-semibold text-right">Amount (৳)</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, index) => (
              <tr key={index} className="border-t">
                <td className="p-2 border">{index + 1}</td>
                <td className="p-2 border">{row.serviceId || "N/A"}</td>
                <td className="p-2 border text-right">{parseFloat(row.unitPrice || 0).toFixed(2)}</td>
                <td className="p-2 border text-right">{row.totalUnit || "0"}</td>
                <td className="p-2 border text-right">{parseFloat(row.amount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );

      const summary = (
        <div className="flex flex-col text-xl">
          <div className="flex justify-between text-gray-600"><span>Bill Amount:</span><span>{Math.round(billResponse?.billAmount || 0).toFixed(2)}</span></div>
          <div className="flex justify-between text-gray-600"><span>Discount:</span><span>{Math.round(billResponse?.discount || 0).toFixed(2)}</span></div>
          <div className="flex justify-between text-gray-600"><span>Net Amount:</span><span>{Math.round(billResponse?.netAmount || 0).toFixed(2)}</span></div>
          <div className="flex justify-between text-gray-600"><span>Paid Amount:</span><span>{Math.round(billResponse?.paidAmount || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-xl">Payable Amount:</span><span className="text-xl">{Math.round(billResponse?.payableAmount || 0).toFixed(2)}</span></div>
        </div>
      );

      const newPrintData = { billInfo, table, summary, signatureFooter };
      setPrintData(newPrintData);
      setShowPrintPreview(true);

    const billsResp = await axios.get(
        `${API_BASE_URL}/hospital/hospital-bills`,
        {
          headers: { "x-api-key": API_KEY }
        },
      );
      const billsData = Array.isArray(billsResp.data)
        ? billsResp.data
        : billsResp.data.bills || [];
      setAllBills(billsData);

      setTimeout(async () => {
        const elementsToHide = document.querySelectorAll(".print-hidden, .print-hidden-in-print");
        elementsToHide.forEach((el) => (el.style.display = "none"));

        const printLayout = document.querySelector(".hospital-bill-print-preview");
        if (printLayout) {
          printLayout.style.display = "block";
        }

        window.print();

        setTimeout(() => {
          elementsToHide.forEach((el) => (el.style.display = ""));
          if (printLayout) {
            printLayout.style.display = "none";
          }
          setShowPrintPreview(false);
          onSave(savedBill, true);
        }, 500);
      }, 1000);
    } catch (error) {
      console.error("Error saving/updating bill:", error);
      setErrors({ apiError: error.response?.data?.error || "Failed to save/update bill" });
      toast.error("Failed to save/update bill", { autoClose: 2000 });
    } finally {
      setIsLoading(false);
      setProcessingAction(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white shadow-lg w-full max-w-full max-h-[100vh] h-full overflow-y-auto print-wrapper">
        <div className="bg-[#ececec] p-5 flex flex-col md:flex-row justify-between items-center mb-4 border-b border-dashed border-gray-300">
          <div className="flex flex-col items-center md:items-start">
            <h2 className="text-xl font-semibold text-gray-700">
              {selectedBillId ? "Edit Hospital Bill" : "Generate Hospital Bill"}
            </h2>
          </div>
          <div className="flex items-start justify-center flex-wrap gap-2 mt-3 xl:mt-0 relative z-50">
            <div className="w-80 relative">
              <DynamicSelect
                options={patients}
                value={
                  patients.find(
                    (option) =>
                      option.value === formData.patientId ||
                      option.nameOnly === formData.patientName,
                  ) ||
                  (formData.patientName
                    ? {
                        label: formData.patientName,
                        value: formData.patientName,
                      }
                    : null)
                }
                onChange={handleSelectChange("patientName")}
                isSearchable
                placeholder="Search Patient (Name, Reg No, Phone)"
                className={`w-full rounded-md border ${
                  errors.patientName ? "border-red-500" : "border-gray-300"
                } bg-gray-50 shadow-sm`}
                filterOption={(option, inputValue) => {
                  if (!inputValue) return true;
                  const search = inputValue.toLowerCase();
                  const { label } = option;

                  return (
                    label.toLowerCase().includes(search) ||
                    (option.nameOnly && option.nameOnly.toLowerCase().includes(search)) ||
                    (option.phoneNumber && option.phoneNumber.toString().includes(search)) ||
                    (option.regNo && option.regNo.toLowerCase().includes(search))
                  );
                }}
              />
              {errors.patientName && (
                <p className="text-red-500 text-xs mt-1.5">
                  {errors.patientName}
                </p>
              )}
            </div>
            <div className="relative flex items-center gap-2">
              <input
                type="text"
                name="regNoSearch"
                value={regNoSearch}
                onChange={handleInputChange}
                ref={searchRef}
                className={`w-64 p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 bg-gray-50 shadow-sm`}
                placeholder="Search by Reg No (e.g. HR-101)"
                autoComplete="off"
              />

              {/* Dropdown List */}
              {regNoSearch && regNoOptions.length > 0 && (
                <ul className="absolute top-full left-0 w-64 bg-white border border-gray-300 mt-1 rounded-md shadow-lg z-50 max-h-40 overflow-y-auto">
                  {regNoOptions.map((regNo, index) => (
                    <li
                      key={index}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleSelectRegNo(regNo)}
                    >
                      {regNo}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              title="Close"
              className="size-7 rounded-full bg-white text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
              onClick={onClose}
              disabled={isLoading}
            >
              <RiCloseLargeFill />
            </button>
          </div>
        </div>
        <div className="my-4 px-6 h-[calc(100vh-150px)] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              <div>
                <label className="label font-medium text-black">
                  Special No
                </label>
                <input
                  type="text"
                  name="specialNo"
                  value={formData.specialNo}
                  onChange={handleInputChange}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.specialNo ? "border-red-500" : "border-gray-300"
                  } ${selectedBillId ? "bg-gray-100" : ""}`}
                  placeholder="Enter Special No"
                  disabled={!!selectedBillId}
                />
                {errors.specialNo && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.specialNo}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">Reg No</label>
                <input
                  type="text"
                  name="regNo"
                  value={formData.regNo}
                  onChange={handleInputChange}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.regNo ? "border-red-500" : "border-gray-300"
                  } ${selectedBillId ? "bg-gray-100" : ""}`}
                  placeholder="Enter Reg No (e.g., MH_PB_00001)"
                  disabled={!!selectedBillId}
                />
                {errors.regNo && (
                  <p className="text-red-500 text-xs mt-1.5">{errors.regNo}</p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">
                  Admission Date
                </label>
                <DatePicker
                  selected={
                    formData.admissionDate
                      ? new Date(formData.admissionDate)
                      : null
                  }
                  onChange={(date) => {
                    setFormData((prev) => ({
                      ...prev,
                      admissionDate: date ? getLocalDateString(date) : "",
                    }));
                  }}
                  dateFormat="dd MMMM yyyy"
                  placeholderText="Select Admission Date"
                  disabled={!!selectedBillId}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    selectedBillId ? "bg-gray-100" : "bg-white"
                  }`}
                />
                {errors.admissionDate && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.admissionDate}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">
                  Billing Date
                </label>
                <DatePicker
                  selected={
                    formData.billingDate ? new Date(formData.billingDate) : null
                  }
                  onChange={(date) => {
                    setFormData((prev) => ({
                      ...prev,
                      billingDate: date ? getLocalDateString(date) : "",
                    }));
                  }}
                  dateFormat="dd MMMM yyyy"
                  placeholderText="Select Billing Date"
                  disabled={!!selectedBillId}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    selectedBillId ? "bg-gray-100" : "bg-white"
                  }`}
                />
                {errors.billingDate && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.billingDate}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">Refer</label>
                <DynamicSelect
                  options={agents}
                  value={
                    agents.find((option) => option.label === formData.refer) ||
                    null
                  }
                  onChange={handleSelectChange("refer")}
                  isSearchable
                  placeholder="Select Refer"
                  className={`w-full rounded-md border ${
                    errors.refer ? "border-red-500" : "border-gray-300"
                  } bg-gray-50 shadow-sm`}
                />
                {errors.refer && (
                  <p className="text-red-500 text-xs mt-1.5">{errors.refer}</p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">
                  Refer Amount
                </label>
                <input
                  type="number"
                  name="referAmount"
                  value={formData.referAmount}
                  onChange={handleInputChange}
                  className={`w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.referAmount ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter refer amount"
                  min="0"
                />
                {errors.referAmount && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.referAmount}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">
                  Authorized By <span className="text-red-600">*</span>
                </label>
                <DynamicSelect
                  options={authorizedByOptions}
                  value={
                    formData.authorizedBy
                      ? authorizedByOptions.find(
                          (option) => option.value === formData.authorizedBy,
                        )
                      : null
                  }
                  onChange={handleSelectChange("authorizedBy")}
                  isSearchable
                  placeholder="Select Authorizer"
                  className={`w-full rounded-md border ${
                    errors.authorizedBy ? "border-red-500" : "border-gray-300"
                  } bg-gray-50 shadow-sm`}
                />
                {errors.authorizedBy && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.authorizedBy}
                  </p>
                )}
              </div>
              <div>
                <label className="label font-medium text-black">
                  Consultant Name
                </label>
                <DynamicSelect
                  options={consultants}
                  value={
                    formData.consultantName
                      ? consultants.find(
                          (c) => c.label === formData.consultantName,
                        )
                      : null
                  }
                  onChange={handleSelectChange("consultantName")}
                  isSearchable
                  placeholder="Select Consultant"
                  className="w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm"
                />
              </div>
            </div>
            <div className="overflow-x-auto mt-6">
              <h2 className="text-lg font-bold text-gray-700 mb-3">Services</h2>
              <table className="w-full table-fixed border-collapse min-w-[1000px] mb-5">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium w-auto">
                      Service Name <span className="text-red-600">*</span>
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Unit Price <span className="text-red-600">*</span>
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Total Unit <span className="text-red-600">*</span>
                    </th>
                    <th className="text-left text-xs text-black border border-gray-300 py-2 px-3 font-medium">
                      Amount
                    </th>
                    <th className="text-center text-xs text-black border border-gray-300 py-2 px-3 w-16 font-medium">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index}>
                      <td className="border border-gray-200 py-2 px-3">
                        <DynamicSelect
                          options={services}
                          value={
                            services.find(
                              (service) => service.label === row.serviceId,
                            ) || null
                          }
                          onChange={handleServiceChange(index)}
                          placeholder="Service"
                          isSearchable
                          className={`w-full rounded-md border ${
                            errors.serviceId
                              ? "border-red-500"
                              : "border-gray-300"
                          } bg-gray-50 shadow-sm`}
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          name="unitPrice"
                          value={row.unitPrice}
                          onChange={(e) => handleTableInputChange(index, e)}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                          min="0"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          name="totalUnit"
                          value={row.totalUnit}
                          onChange={(e) => handleTableInputChange(index, e)}
                          className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.totalUnit
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          placeholder="0"
                          min="0"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <input
                          type="number"
                          name="amount"
                          value={Math.round(row.amount)}
                          readOnly
                          className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                          placeholder="0"
                        />
                      </td>
                      <td className="border border-gray-200 py-2 px-3">
                        <button
                          title="Remove"
                          className="size-7 mx-auto rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          onClick={() => handleRemoveRow(index)}
                          disabled={tableData.length === 1}
                        >
                          <RiDeleteBinLine />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Bill Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.billAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <button
                        title="Add Row"
                        className="size-7 mx-auto rounded-full bg-[#73CB64] text-white text-sm flex items-center justify-center hover:bg-M-primary-color transition-all duration-300"
                        onClick={handleAddRow}
                      >
                        <RiAddFill />
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Service Charge (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        name="serviceCharge"
                        value={serviceCharge}
                        onChange={handleOtherInputChange(setServiceCharge)}
                        className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.serviceCharge
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      {errors.serviceCharge && (
                        <p className="text-red-500 text-xs mt-1.5">
                          {errors.serviceCharge}
                        </p>
                      )}
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Total Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.totalAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Discount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        name="discount"
                        value={discount}
                        onChange={handleOtherInputChange(setDiscount)}
                        readOnly={!!selectedBillId}
                        className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.discount ? "border-red-500" : "border-gray-300"
                        }
                          ${selectedBillId ? "bg-gray-100 cursor-not-allowed" : ""}
                          `}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      {errors.discount && (
                        <p className="text-red-500 text-xs mt-1.5">
                          {errors.discount}
                        </p>
                      )}
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Net Amount (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.netAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Paid (৳) <span className="text-red-600">*</span>
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        name="paidAmount"
                        value={paidAmount}
                        onChange={handleOtherInputChange(setPaidAmount)}
                        readOnly={!!selectedBillId}
                        className={`w-full p-2 border rounded-md text-sm text-end focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.paidAmount
                            ? "border-red-500"
                            : "border-gray-300"
                        }
                          ${selectedBillId ? "bg-gray-100 cursor-not-allowed" : ""}
                          `}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      {errors.paidAmount && (
                        <p className="text-red-500 text-xs mt-1.5">
                          {errors.paidAmount}
                        </p>
                      )}
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                  <tr>
                    <td
                      className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium"
                      colSpan={3}
                    >
                      Payable (৳)
                    </td>
                    <td className="border border-gray-200 py-2 px-3">
                      <input
                        type="number"
                        value={Math.round(billResponse?.payableAmount) || 0}
                        readOnly
                        className="w-full p-2 border border-gray-300 rounded-md text-sm text-end bg-gray-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-200 py-2 px-3"></td>
                  </tr>
                </tbody>
              </table>
              {errors.apiError && (
                <p className="text-red-500 text-xs mt-1.5">{errors.apiError}</p>
              )}
            </div>
          </form>
        </div>
        <div className="flex justify-end items-center gap-3 px-6 fixed w-full bottom-0 p-3 bg-gray-200 z-20 border-t border-dashed border-M-text-color/50">
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="bg-gray-600 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-gray-800 transition-all duration-300"
              disabled={isLoading}
            >
              Reset
            </button>
            <button
              onClick={onClose}
              className="bg-[#E7633D]/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndPrint}
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {processingAction === "print"
                ? selectedBillId
                  ? "Updating..."
                  : "Saving..."
                : selectedBillId
                  ? "Update & Print"
                  : "Save & Print"}
            </button>
            <button
              onClick={handleSubmit}
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-3 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {processingAction === "save"
                ? selectedBillId
                  ? "Updating..."
                  : "Saving..."
                : selectedBillId
                  ? "Update"
                  : "Save"}
            </button>
          </div>
        </div>
        {showPrintPreview && printData && (
          <div className="hospital-bill-print-preview">
            <PrintBillLayoutA5
              className="hospital-bill-print-content"
              title="Hospital Bill"
              billInfo={printData.billInfo}
              table={printData.table}
              summary={printData.summary}
              footer={printData.signatureFooter}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default HospitalBillModal;